import { useState, useEffect, CSSProperties } from "react";

const SUPABASE_URL = 'https://szbwqnllhhsawebwhrwj.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN6YndxbmxsaGhzYXdlYndocndqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI1MDAzNDgsImV4cCI6MjA4ODA3NjM0OH0.hRIxkoL41qrAYKaVt1iND9ZjILyDpAM5k0TG8lfqKrQ';

const T = {
  burgundy: "#4A0E0E",
  burgundyMid: "#6B1A1A",
  burgundyLight: "#8B2A2A",
  cream: "#F5F0E8",
  creamMuted: "#C8B89A",
  copper: "#8B6B4A",
  copperLight: "#B08A60",
  ink: "#0A0806",
  surface: "#120E0A",
  charcoalLight: "#3A2A22",
} as const;

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Crimson+Pro:ital,wght@0,200;0,300;0,400;1,200;1,300&family=Josefin+Sans:wght@100;200;300&display=swap');

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    background: #0A0806;
    color: #F5F0E8;
    font-family: 'Crimson Pro', Georgia, serif;
    -webkit-font-smoothing: antialiased;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.35; }
    50% { opacity: 1; }
  }

  .sl-input {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid #2A1A14;
    color: #F5F0E8;
    font-family: 'Crimson Pro', Georgia, serif;
    font-size: 16px;
    font-weight: 300;
    padding: 10px 0;
    outline: none;
    caret-color: #B08A60;
    transition: border-color 0.35s ease;
  }

  .sl-input::placeholder {
    color: #4A3020;
    font-style: italic;
  }

  .sl-input:focus {
    border-bottom-color: #8B6B4A;
  }

  .sl-input.has-error {
    border-bottom-color: #8B2A2A;
  }

  .sl-cta {
    position: relative;
    overflow: hidden;
    width: 100%;
    background: #4A0E0E;
    border: 1px solid #6B1A1A;
    color: #F5F0E8;
    font-family: 'Josefin Sans', sans-serif;
    font-weight: 300;
    font-size: 11px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 17px 48px;
    cursor: pointer;
    transition: background 0.4s ease, transform 0.2s ease;
  }

  .sl-cta:hover {
    background: #6B1A1A;
    transform: translateY(-1px);
  }

  .sl-cta:disabled {
    opacity: 0.38;
    cursor: not-allowed;
    transform: none;
  }

  .sl-tab {
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'Josefin Sans', sans-serif;
    font-weight: 200;
    font-size: 10px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    padding: 10px 0;
    transition: color 0.3s ease;
    border-bottom: 1px solid transparent;
  }

  .sl-tab.active {
    border-bottom-color: #8B6B4A;
  }

  .anim-fade-up {
    animation: fadeUp 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  }

  html, body, #root {
    width: 100%;
    min-height: 100%;
    overflow-x: hidden;
  }

  @media (max-width: 480px) {
    .auth-page {
      padding: 32px 16px !important;
      justify-content: flex-start !important;
      padding-top: 56px !important;
    }

    .auth-logo {
      margin-bottom: 32px !important;
    }

    .auth-card {
      max-width: 100% !important;
      padding: 28px 20px !important;
    }

    .auth-tabs {
      gap: 24px !important;
      margin-bottom: 28px !important;
      overflow-x: auto !important;
      white-space: nowrap !important;
    }

    .auth-role-toggle {
      flex-direction: column !important;
    }

    .sl-cta {
      padding: 15px 20px !important;
      letter-spacing: 0.26em !important;
    }
  }
`;

type Mode = "login" | "signup";
type Role = "producer" | "expert";

async function supabaseSignup(email: string, password: string) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.msg || data.error_description || "Signup failed");
  }

  return data;
}

async function supabaseLogin(email: string, password: string) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.msg || data.error_description || "Login failed");
  }

  return data;
}

async function supabaseUpdateProfile(userId: string, role: Role, token: string) {
  await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${token}`,
      Prefer: "return=minimal",
    },
    body: JSON.stringify({ role }),
  });
}
async function supabaseFetchProfile(userId: string, token: string) {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=id,email,role,full_name&limit=1`,
    {
      method: "GET",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.message || data.error || "Failed to fetch profile");
  }

  return Array.isArray(data) ? data[0] : null;
}
export default function AuthPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [role, setRole] = useState<Role>("producer");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const tag = document.createElement("style");
    tag.innerHTML = GLOBAL_CSS;
    document.head.appendChild(tag);

    return () => {
      document.head.removeChild(tag);
    };
  }, []);

  const labelStyle: CSSProperties = {
    display: "block",
    fontFamily: "'Josefin Sans', sans-serif",
    fontWeight: 100,
    fontSize: 9,
    letterSpacing: "0.4em",
    textTransform: "uppercase",
    color: T.copper,
    marginBottom: 8,
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    if (mode === "signup" && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (mode === "signup" && password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    try {
      if (mode === "signup") {
        const data = await supabaseSignup(email, password);

        if (data.user && data.access_token) {
          await supabaseUpdateProfile(data.user.id, role, data.access_token);
        }

        setSuccess("Account created! Check your email to confirm, then log in.");
        setMode("login");
      } else {
        const data = await supabaseLogin(email, password);

localStorage.setItem("sl_token", data.access_token);

const decodedToken = JSON.parse(atob(data.access_token.split(".")[1]));
const userId = data.user?.id || decodedToken.sub || "";

const profile = await supabaseFetchProfile(userId, data.access_token);

const userRole: Role = profile?.role === "expert" ? "expert" : "producer";

const userData = {
  id: userId,
  email: data.user?.email || profile?.email || email,
  role: userRole,
  full_name: profile?.full_name || "",
  user_metadata: data.user?.user_metadata || {},
};

localStorage.setItem("sl_user", JSON.stringify(userData));

window.location.href =
  userRole === "expert" ? "/expert-dashboard" : "/producer-dashboard";
}
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-page"
      style={{
        minHeight: "100vh",
        background: T.ink,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
      }}
    >
      <div
        className="anim-fade-up auth-logo"
        style={{ marginBottom: 48, textAlign: "center" }}
      >
        <span
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: 28,
            color: T.cream,
            letterSpacing: "0.08em",
          }}
        >
          SipLogic
        </span>

        <div
          style={{
            marginTop: 8,
            fontFamily: "'Josefin Sans', sans-serif",
            fontWeight: 100,
            fontSize: 9,
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            color: T.copper,
          }}
        >
          Georgia&apos;s Tasting Marketplace
        </div>
      </div>

      <div
        className="anim-fade-up auth-card"
        style={{
          width: "100%",
          maxWidth: 420,
          background: T.surface,
          border: `1px solid ${T.charcoalLight}`,
          padding: "40px 36px",
        }}
      >
        <div
          className="auth-tabs"
          style={{
            display: "flex",
            gap: 32,
            marginBottom: 36,
            borderBottom: `1px solid ${T.charcoalLight}`,
          }}
        >
          {(["login", "signup"] as Mode[]).map((m) => (
            <button
              key={m}
              className={`sl-tab${mode === m ? " active" : ""}`}
              style={{ color: mode === m ? T.creamMuted : T.copper }}
              onClick={() => {
                setMode(m);
                setError("");
                setSuccess("");
              }}
            >
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        {mode === "signup" && (
          <div style={{ marginBottom: 28 }}>
            <p style={labelStyle}>I am a</p>

            <div
              className="auth-role-toggle"
              style={{ display: "flex", gap: 8 }}
            >
              {(["producer", "expert"] as Role[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: role === r ? "#1E0E0A" : "transparent",
                    border: `1px solid ${
                      role === r ? T.burgundyMid : T.charcoalLight
                    }`,
                    color: role === r ? T.cream : T.copper,
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontWeight: 200,
                    fontSize: 9,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                  }}
                >
                  {r === "producer" ? "Producer / Brand" : "Expert / Sommelier"}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <div>
            <label style={labelStyle}>Email</label>
            <input
              className="sl-input"
              type="email"
              placeholder="you@yourbrand.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>

          <div>
            <label style={labelStyle}>Password</label>
            <input
              className="sl-input"
              type="password"
              placeholder="········"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
          </div>

          {mode === "signup" && (
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input
                className="sl-input"
                type="password"
                placeholder="········"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          )}
        </div>

        {error && (
          <p
            style={{
              marginTop: 20,
              fontFamily: "'Crimson Pro', serif",
              fontSize: 14,
              color: T.burgundyLight,
              fontStyle: "italic",
            }}
          >
            {error}
          </p>
        )}

        {success && (
          <p
            style={{
              marginTop: 20,
              fontFamily: "'Crimson Pro', serif",
              fontSize: 14,
              color: T.copperLight,
              fontStyle: "italic",
            }}
          >
            {success}
          </p>
        )}

        <button
          className="sl-cta"
          onClick={handleSubmit}
          disabled={loading}
          style={{ marginTop: 32 }}
        >
          {loading ? (
            <span style={{ animation: "pulse 1s ease-in-out infinite" }}>
              ◈ &nbsp; {mode === "login" ? "Signing in…" : "Creating account…"}
            </span>
          ) : mode === "login" ? (
            "Sign In"
          ) : (
            "Create Account"
          )}
        </button>

        <p
          style={{
            marginTop: 20,
            textAlign: "center",
            fontFamily: "'Crimson Pro', serif",
            fontSize: 14,
            color: T.copper,
            fontStyle: "italic",
          }}
        >
          {mode === "login" ? "New to SipLogic? " : "Already have an account? "}

          <span
            onClick={() => {
              setMode(mode === "login" ? "signup" : "login");
              setError("");
            }}
            style={{
              color: T.creamMuted,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </span>
        </p>
      </div>

      <p
        style={{
          marginTop: 24,
          fontFamily: "'Josefin Sans', sans-serif",
          fontWeight: 100,
          fontSize: 9,
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: T.charcoalLight,
          cursor: "pointer",
        }}
        onClick={() => (window.location.href = "/")}
      >
        ← Back to SipLogic
      </p>
    </div>
  );
}